import { useState } from "react";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { EmptyState } from "@/shared/components/EmptyState";
import { Button } from "@/shared/components/button";
import { NotAvailable } from "@/shared/components/NotAvailable";
import type { DiagnosisRecord } from "@/features/patient/clinical-history/utils/adaptClinicalHistory";
import { cn } from "@/shared/lib/utils";
import { PageBackButton } from "@/shared/components/PageBackButton";

type Diagnosis = DiagnosisRecord;

interface DiagnosisSectionProps {
  diagnoses: DiagnosisRecord[];
  isLoading?: boolean;
}

const statusLabels: Record<string, string> = {
  active: "Active",
  chronic: "Chronic",
  resolved: "Resolved",
};

// Gray tags for detail page only
const grayTag = "bg-[#F3F4F6] !text-[#6B7280]";

function formatDate(dateString: string): string {
  if (!dateString) return "Not available";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "Not available";
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function DiagnosisTag({ children }: { children: React.ReactNode }) {
  return (
    <span className={cn("rounded-full px-2.5 py-1 text-[11px] font-extrabold capitalize", grayTag)}>
      {children}
    </span>
  );
}

// Smaller icons
function RedIcon({ icon, size = "md" }: { icon: string; size?: "sm" | "md" | "lg" }) {
  return (
    <div className={cn(
      "flex shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50",
      size === "sm" && "h-7 w-7",
      size === "md" && "h-9 w-9",
      size === "lg" && "h-11 w-11"
    )}>
      <Icon icon={icon} className={cn(
        "!text-white",
        size === "sm" && "h-3 w-3",
        size === "md" && "h-4 w-4",
        size === "lg" && "h-5 w-5"
      )} />
    </div>
  );
}

export function DiagnosisSection({ diagnoses, isLoading = false }: DiagnosisSectionProps) {
  const [selectedDiagnosis, setSelectedDiagnosis] = useState<Diagnosis | null>(null);

  if (isLoading) {
    return (
      <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <div className="mb-5 grid gap-5 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="min-h-[110px] rounded-3xl border border-[#E5E7EB] bg-white px-6 py-5 shadow-sm">
              <Skeleton active paragraph={{ rows: 1 }} />
            </div>
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-3xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
              <Skeleton active avatar paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const activeDiagnoses   = diagnoses.filter(d => d.status === "active" || d.status === "chronic");
  const resolvedDiagnoses = diagnoses.filter(d => d.status === "resolved");
  const activeCount       = diagnoses.filter(d => d.status === "active").length;
  const chronicCount      = diagnoses.filter(d => d.status === "chronic").length;

  // ── Detail page ──────────────────────────────────────────────────────────
  if (selectedDiagnosis) {
    const facts: { icon: string; label: string; value: React.ReactNode }[] = [
      { icon: "lucide:calendar",     label: "Diagnosed",    value: formatDate(selectedDiagnosis.diagnosedDate) },
      { icon: "lucide:user",         label: "Diagnosed By", value: selectedDiagnosis.diagnosedBy || <NotAvailable /> },
      { icon: "lucide:alert-circle", label: "Severity",     value: selectedDiagnosis.severity || <NotAvailable /> },
    ];

    return (
      <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <PageBackButton onClick={() => setSelectedDiagnosis(null)} ariaLabel="Back to all diagnoses" className="!mb-5" />

        {/* Header card */}
        <section className="mb-5 rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm lg:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <RedIcon icon="lucide:stethoscope" size="lg" />
              <div className="min-w-0">
                {/* Tags — gray, only on detail page */}
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <DiagnosisTag>{statusLabels[selectedDiagnosis.status]}</DiagnosisTag>
                  <DiagnosisTag>{selectedDiagnosis.severity || "Severity not recorded"}</DiagnosisTag>
                </div>
                <h1 className="break-words text-2xl font-extrabold leading-tight tracking-tight text-[#0A2947]">
                  {selectedDiagnosis.name}
                </h1>
                <p className="mt-1 text-sm font-semibold text-[#9CA3AF]">
                  ICD-10: {selectedDiagnosis.icdCode || "Not available"}
                </p>
              </div>
            </div>
            <div className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
              <p className="text-[10px] font-extrabold uppercase tracking-wide text-[#9CA3AF]">Related Records</p>
              <p className="mt-1 text-2xl font-extrabold text-[#0A2947]">{selectedDiagnosis.relatedRecords.length}</p>
            </div>
          </div>
        </section>

        <div className="grid gap-5 lg:grid-cols-3">
          <div className="space-y-5 lg:col-span-2">

            {/* Plain language summary */}
            <section className="rounded-3xl border-2 border-[#F7B4C3] bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center gap-3">
                <RedIcon icon="lucide:info" size="sm" />
                <h2 className="text-base font-extrabold tracking-tight text-[#0A2947]">
                  What This Means for You
                </h2>
              </div>
              <p className="text-sm font-medium leading-7 text-[#6B7280]">
                {selectedDiagnosis.plainLanguageSummary || (
                  <NotAvailable label="No plain-language summary has been generated for this diagnosis yet." />
                )}
              </p>
            </section>

            {/* Medical description */}
            <section className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
              <h2 className="mb-3 text-base font-extrabold tracking-tight text-[#0A2947]">
                Medical Description
              </h2>
              <p className="text-sm font-medium leading-7 text-[#6B7280]">
                {selectedDiagnosis.description || (
                  <NotAvailable label="No clinical description was included with this record." />
                )}
              </p>
            </section>

            {/* Care context */}
            <section className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-base font-extrabold tracking-tight text-[#0A2947]">
                Care Context
              </h2>
              <div className="grid gap-3 md:grid-cols-3">
                {[
                  ["Status",   statusLabels[selectedDiagnosis.status]],
                  ["Severity", selectedDiagnosis.severity || "Not available"],
                  ["ICD-10",   selectedDiagnosis.icdCode || "Not available"],
                ].map(([label, value], index) => (
                  <div key={label} className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
                    <span className={cn(
                      "mb-3 block h-2 w-2 rounded-full",
                      index === 0 ? "bg-[#E4002B]/50" : "bg-[#0A2947]"
                    )} />
                    <p className="text-[10px] font-extrabold uppercase tracking-wide text-[#9CA3AF]">{label}</p>
                    <p className="mt-1 text-sm font-extrabold capitalize text-[#0A2947]">{value}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="space-y-5">
            <section className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-base font-extrabold tracking-tight text-[#0A2947]">Quick Facts</h2>
              <div className="space-y-4">
                {facts.map(({ icon, label, value }) => (
                  <div key={label} className="flex items-center gap-3">
                    <RedIcon icon={icon} size="sm" />
                    <div>
                      <p className="text-[10px] font-extrabold uppercase tracking-wide text-[#9CA3AF]">{label}</p>
                      <p className="mt-0.5 text-sm font-bold capitalize text-[#0A2947]">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
              <h2 className="mb-3 text-base font-extrabold tracking-tight text-[#0A2947]">Related Records</h2>
              <p className="text-sm font-medium leading-6 text-[#6B7280]">
                {selectedDiagnosis.relatedRecords.length} record{selectedDiagnosis.relatedRecords.length !== 1 ? "s" : ""} linked to this diagnosis.
              </p>
              <Button
                variant="outline"
                className="!mt-4 !flex !h-10 !w-full !items-center !justify-center !gap-2 !rounded-full !border-[#E5E7EB] !bg-white !text-sm !font-bold !text-[#9CA3AF] hover:!border-[#D1D5DB] hover:!bg-[#F3F4F6] hover:!text-[#9CA3AF]"
              >
                <Icon icon="lucide:file-text" className="h-4 w-4 text-[#9CA3AF]" />
                View Related Records
              </Button>
            </section>
          </aside>
        </div>
      </div>
    );
  }

  // ── List page ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">

      {/* Stats cards — minimal dashboard style */}
      <div className="mb-5 grid gap-5 sm:grid-cols-3">
        {[
          { label: "Active Diagnoses",   value: activeCount             },
          { label: "Chronic Diagnoses",  value: chronicCount            },
          { label: "Resolved Diagnoses", value: resolvedDiagnoses.length },
        ].map(({ label, value }) => (
          <div
            key={label}
            className="flex cursor-default flex-col justify-between min-h-[110px] rounded-3xl border border-[#E5E7EB] bg-white px-6 py-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            <p className="text-sm font-normal text-[#9CA3AF]">{label}</p>
            <div className="flex justify-end">
              <p className="text-3xl font-bold text-[#111827]">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Active / Chronic diagnoses — NO tags on listing */}
      {activeDiagnoses.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-3 text-base font-extrabold tracking-tight text-[#0A2947]">Current Diagnoses</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {activeDiagnoses.map((diagnosis) => (
              <Button
                key={diagnosis.id}
                variant="outline"
                onClick={() => setSelectedDiagnosis(diagnosis)}
                className="!flex !h-auto !items-start !gap-3 !rounded-3xl !border-[#E5E7EB] !bg-white !p-4 !text-left !shadow-sm hover:!border-[#F36F8F]/50 hover:!bg-white hover:!shadow-md"
              >
                <RedIcon icon="lucide:stethoscope" size="md" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="truncate text-sm font-extrabold text-[#0A2947]">{diagnosis.name}</p>
                    <Icon icon="lucide:chevron-right" className="h-4 w-4 shrink-0 text-[#9CA3AF]" />
                  </div>
                  <p className="mt-0.5 text-xs font-semibold text-[#9CA3AF]">ICD-10: {diagnosis.icdCode || "Not available"}</p>
                  <p className="mt-2 line-clamp-2 text-xs font-medium leading-5 text-[#6B7280]">
                    {diagnosis.plainLanguageSummary || diagnosis.description || (
                      <NotAvailable label="No summary available" />
                    )}
                  </p>
                </div>
              </Button>
            ))}
          </div>
        </section>
      )}

      {/* Resolved diagnoses — NO tags on listing */}
      {resolvedDiagnoses.length > 0 && (
        <section>
          <h2 className="mb-3 text-base font-extrabold tracking-tight text-[#0A2947]">Resolved Diagnoses</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {resolvedDiagnoses.map((diagnosis) => (
              <Button
                key={diagnosis.id}
                variant="outline"
                onClick={() => setSelectedDiagnosis(diagnosis)}
                className="!flex !h-auto !items-center !gap-3 !rounded-3xl !border-[#E5E7EB] !bg-white !p-4 !text-left !shadow-sm hover:!border-[#F36F8F]/50 hover:!bg-white hover:!shadow-md"
              >
                <RedIcon icon="lucide:check-circle-2" size="md" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-extrabold text-[#0A2947]">{diagnosis.name}</p>
                  <p className="mt-0.5 text-xs font-semibold text-[#9CA3AF]">
                    Resolved — Diagnosed {formatDate(diagnosis.diagnosedDate)}
                  </p>
                </div>
                <Icon icon="lucide:chevron-right" className="h-4 w-4 shrink-0 text-[#9CA3AF]" />
              </Button>
            ))}
          </div>
        </section>
      )}

      {/* Empty state */}
      {diagnoses.length === 0 && (
        <EmptyState
          icon={<Icon icon="lucide:stethoscope" className="h-12 w-12 text-[#9CA3AF]" />}
          title="No diagnoses recorded"
          description="Your diagnoses will appear here once added to your records"
        />
      )}
    </div>
  );
}
